import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import Header from "@/components/Header";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { FileSpreadsheet, Plus, Pencil, Trash2, Activity, Users, FileText, ClipboardList, Eye } from "lucide-react";
import { toast } from "sonner";
import ConsejoDashboard from "@/pages/ConsejoDashboard";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [docTypes, setDocTypes] = useState([]);
  const [logs, setLogs] = useState([]);
  const [editType, setEditType] = useState(null);
  const [typeForm, setTypeForm] = useState({ name: "", description: "", required: true, active: true });
  const [tab, setTab] = useState("overview");

  const load = async () => {
    const [s, u, t, l] = await Promise.all([
      api.get("/dashboard/stats"),
      api.get("/users"),
      api.get("/document-types"),
      api.get("/logs"),
    ]);
    setStats(s.data); setUsers(u.data); setDocTypes(t.data); setLogs(l.data);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditType({ id: null }); setTypeForm({ name: "", description: "", required: true, active: true }); };
  const openEdit = (t) => { setEditType(t); setTypeForm({ name: t.name, description: t.description || "", required: !!t.required, active: !!t.active }); };

  const saveType = async () => {
    if (!typeForm.name || !typeForm.name.trim()) {
      toast.error("El nombre es obligatorio");
      return;
    }
    try {
      if (editType?.id) {
        await api.put(`/document-types/${editType.id}`, typeForm);
        toast.success("Solicitud actualizada");
      } else {
        await api.post("/document-types", typeForm);
        toast.success("Solicitud creada");
      }
      setEditType(null);
      load();
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message || "Error desconocido";
      console.error("saveType error:", e.response?.status, e.response?.data, e);
      toast.error(`Error: ${msg}`);
    }
  };

  const deleteType = async (id) => {
    if (!confirm("¿Eliminar esta solicitud de documento?")) return;
    try {
      await api.delete(`/document-types/${id}`);
      toast.success("Solicitud eliminada");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error al eliminar");
    }
  };

  const deleteUser = async (id) => {
    if (!confirm("¿Eliminar este usuario y todos sus documentos?")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("Usuario eliminado");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error al eliminar");
    }
  };

  const exportExcel = async () => {
    const res = await api.get("/export/excel", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = "cumplimiento_dop_vera.xlsx"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-heading font-bold text-stone-900">Panel Administrador</h1>
            <p className="mt-1 text-stone-600 text-sm">Control total del sistema documental</p>
          </div>
          <Button onClick={exportExcel} className="bg-primary hover:bg-primary-hover" data-testid="btn-admin-export">
            <FileSpreadsheet className="h-4 w-4 mr-2" /> Exportar Excel
          </Button>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <KPI icon={Users} label="Productores" value={stats.total_productores} />
            <KPI icon={FileText} label="Documentos" value={stats.total_documents} />
            <KPI icon={ClipboardList} label="Pendientes" value={stats.pending} accent="text-amber-600" />
            <KPI icon={Activity} label="Aprobados" value={stats.approved} accent="text-green-600" />
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-white border border-stone-200 p-1 rounded-xl">
            <TabsTrigger value="overview" data-testid="tab-overview">Resumen</TabsTrigger>
            <TabsTrigger value="consejo-view" data-testid="tab-consejo-view">Vista Consejo</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users">Usuarios</TabsTrigger>
            <TabsTrigger value="types" data-testid="tab-types">Solicitudes de documentos</TabsTrigger>
            <TabsTrigger value="logs" data-testid="tab-logs">Auditoría</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6">
            <Card className="rounded-xl border-stone-200 overflow-hidden">
              <div className="p-5 border-b border-stone-200">
                <h3 className="font-heading font-semibold text-stone-900">Actividad reciente de documentos</h3>
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="bg-stone-50">
                    <TableHead>Documento</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Subido</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(stats?.recent || []).map((d) => (
                    <TableRow key={d.id}>
                      <TableCell><div className="font-medium">{d.document_type_name}</div><div className="text-xs text-stone-500">{d.filename}</div></TableCell>
                      <TableCell><span className="capitalize text-sm">{d.status}</span></TableCell>
                      <TableCell className="text-stone-600 text-sm">{new Date(d.uploaded_at).toLocaleString("es-ES")}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          <TabsContent value="consejo-view" className="mt-6">
            <div className="mb-3 p-3 bg-paprika-50 border border-paprika-200 rounded-lg text-sm text-stone-700 flex items-center gap-2">
              <Eye className="h-4 w-4 text-primary" /> Vista de solo consulta del panel Consejo Regulador. Puedes validar documentos igual que el Consejo.
            </div>
            <div className="-mx-4 md:-mx-8">
              <ConsejoDashboard readOnlyForRole />
            </div>
          </TabsContent>

          <TabsContent value="users" className="mt-6">
            <Card className="rounded-xl border-stone-200 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-stone-50">
                    <TableHead>Nombre</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Rol</TableHead>
                    <TableHead>Empresa</TableHead>
                    <TableHead>DNI/CIF</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id} data-testid={`admin-user-${u.id}`}>
                      <TableCell className="font-medium">{u.name}</TableCell>
                      <TableCell className="text-stone-600">{u.email}</TableCell>
                      <TableCell><span className="capitalize text-xs px-2 py-1 rounded-full bg-stone-100 text-stone-700">{u.role}</span></TableCell>
                      <TableCell>{u.empresa || "—"}</TableCell>
                      <TableCell className="font-mono text-sm">{u.dni_cif}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" onClick={() => deleteUser(u.id)} data-testid={`btn-del-user-${u.id}`}>
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          <TabsContent value="types" className="mt-6">
            <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
              <div>
                <h3 className="text-lg font-heading font-semibold text-stone-900">Solicitudes de documentos</h3>
                <p className="text-sm text-stone-600 mt-0.5">Gestiona qué documentos debe presentar cada productor o industria. Añade, edita o elimina solicitudes.</p>
              </div>
              <Button onClick={openCreate} className="bg-primary hover:bg-primary-hover" data-testid="btn-new-type">
                <Plus className="h-4 w-4 mr-2" /> Nueva solicitud
              </Button>
            </div>
            <Card className="rounded-xl border-stone-200 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-stone-50">
                    <TableHead>Nombre</TableHead>
                    <TableHead>Descripción</TableHead>
                    <TableHead>Obligatorio</TableHead>
                    <TableHead>Activo</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {docTypes.map((t) => (
                    <TableRow key={t.id} data-testid={`type-row-${t.id}`}>
                      <TableCell className="font-medium">{t.name}</TableCell>
                      <TableCell className="text-stone-600 text-sm max-w-md">{t.description}</TableCell>
                      <TableCell>{t.required ? "Sí" : "No"}</TableCell>
                      <TableCell>{t.active ? <span className="text-green-600">Activo</span> : <span className="text-stone-400">Inactivo</span>}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="ghost" onClick={() => openEdit(t)} data-testid={`btn-edit-type-${t.id}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => deleteType(t.id)} data-testid={`btn-del-type-${t.id}`}>
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          <TabsContent value="logs" className="mt-6">
            <Card className="rounded-xl border-stone-200 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-stone-50">
                    <TableHead>Fecha</TableHead>
                    <TableHead>Usuario</TableHead>
                    <TableHead>Acción</TableHead>
                    <TableHead>Detalles</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((l) => (
                    <TableRow key={l.id}>
                      <TableCell className="text-stone-600 text-sm">{new Date(l.created_at).toLocaleString("es-ES")}</TableCell>
                      <TableCell>{l.user?.name || "—"}<div className="text-xs text-stone-500">{l.user?.email}</div></TableCell>
                      <TableCell><span className="text-xs px-2 py-1 rounded-full bg-stone-100 text-stone-700">{l.action}</span></TableCell>
                      <TableCell className="text-xs text-stone-600 font-mono">{JSON.stringify(l.details || {})}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Doc type modal */}
      <Dialog open={!!editType} onOpenChange={(v) => !v && setEditType(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-heading">{editType?.id ? "Editar" : "Nueva"} solicitud de documento</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nombre</Label><Input value={typeForm.name} onChange={(e) => setTypeForm({ ...typeForm, name: e.target.value })} data-testid="input-type-name" className="mt-1.5" /></div>
            <div><Label>Descripción</Label><Textarea value={typeForm.description} onChange={(e) => setTypeForm({ ...typeForm, description: e.target.value })} data-testid="input-type-desc" className="mt-1.5" /></div>
            <div className="flex gap-6">
              <div className="flex items-center gap-2"><Switch checked={typeForm.required} onCheckedChange={(v) => setTypeForm({ ...typeForm, required: v })} /><Label>Obligatorio</Label></div>
              <div className="flex items-center gap-2"><Switch checked={typeForm.active} onCheckedChange={(v) => setTypeForm({ ...typeForm, active: v })} /><Label>Activo</Label></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditType(null)}>Cancelar</Button>
            <Button onClick={saveType} className="bg-primary hover:bg-primary-hover" data-testid="btn-save-type">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KPI({ icon: Icon, label, value, accent = "text-stone-900" }) {
  return (
    <Card className="p-5 rounded-xl border-stone-200">
      <div className="flex items-center justify-between">
        <div className="text-xs text-stone-500 uppercase tracking-wide">{label}</div>
        <Icon className="h-4 w-4 text-stone-400" />
      </div>
      <div className={`text-3xl font-heading font-bold mt-2 ${accent}`}>{value}</div>
    </Card>
  );
}
